import React, { useEffect, useState, useContext } from "react";
import { useImageValidation } from "../../../../customHooks/useImageValidation";
import FacilitiesForm from "../FacilitiesForm/FacilitiesForm";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import importedMunicipalities from "../../../../assets/municipalities.json";
import axios from "axios";
import AdPreviewContext from "../../../../context/AdPreviewContext";
import { Image, Transformation } from "cloudinary-react";
import { v4 as uuidv4 } from "uuid";
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import importedRooms from "../../../../assets/roomTypes.json";
import Loading from "../Loading/Loading";
import { backend } from "../../../../backend";
import { logAxiosError } from "../../../../logging/error";
import WarningModal from "../../../../components/WarningModal/WarningModal";
import { useCallbackPrompt } from "../../../../context/useCallbackPrompt";
import { useAuth } from "../../../../context/AuthContext";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "./editAd.css";
import RoundedButton from "../../../../components/Buttons/RoundedButton/RoundedButton";
import { Helmet } from "react-helmet-async";
import {
  authenticateForm,
  hasValidPrice,
  requiredFieldsCheck,
} from "../../utilities/formAuth";
import { useAutoExpand } from "../../../../customHooks/useAutoExpand";

const defaultFormData = {
  name: "",
  owner_name: "",
  address: "",
  city: "",
  municipality: "",
  zip_code: "",
  type_of_room: "",
  area: "",
  capacity: "",
  workspaces: "",
  facilities: [],
  description: "",
  images: [],
  fully_vacant: true,
  hourly_rent: "",
  daily_rent: "",
  monthly_rent: "",
  workspace_rent: "",
  vat_tax_required: false,
  contract_type: "",
};
const { roomTypes } = importedRooms;
const { municipalities } = importedMunicipalities;

function EditAd(props) {
  const [formData, setFormData] = useState(defaultFormData);
  const [newImages, setNewImages] = useState([]);
  const [imageError, setImageError] = useState(null);
  // These are to prevent user from accessing preview page without necessary info
  const [disableSubmit, setDisableSubmit] = useState(true);

  // Set to true when going to preview, triggers useEffect that navigates to preview
  const [viewPreview, setViewPreview] = useState(false);

  const [pageHeading, setPageHeading] = useState("");
  const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [imageCountError, setImageCountError] = useState("");
  const [invalidFields, setInvalidFields] = useState([]);
  const { adPreviewInfo, setAdPreviewInfo } = useContext(AdPreviewContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const auth = useAuth();
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  // For loading state, while waiting
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [ImageTooBig, setImageTooBig] = useState(null);

  // Warn users when leaving that their ad is not saved
  // enable and disable the prompt with this state
  const [showPrompt, setShowPrompt] = useState(false);
  const {
    showPrompt: showWarningModal,
    confirmNavigation,
    cancelNavigation,
  } = useCallbackPrompt(
    showPrompt,
    "Du har osparade ändringar. Är du säker på att du vill lämna sidan?",
  );
  const { validateFiles, checkMagicBytes, clearValidationErrors } =
    useImageValidation();

  const autoExpand = useAutoExpand();

  const fetchFormData = async () => {
    try {
      const { data } = await axios.get(backend("/api/listings/get-by-id"), {
        params: {
          token: localStorage.getItem("token"),
          id: id,
        },
      });
      if (data.status) {
        setFormData({ ...formData, ...data.ad });
        setPageHeading("Redigera annons");
      }
    } catch (error) {
      logAxiosError(error);
      // TODO: notify user
    }
  };

  useEffect(() => {
    const { info, images, hasChanged } = adPreviewInfo;

    if (hasChanged) {
      setShowPrompt(true);
    }

    // If user returns from preview, set updated info and images
    if (location.state?.from === "preview") {
      setFormData(info);
      setNewImages(images);

      // And reset previewInfo
      setAdPreviewInfo({ info: {}, images: [], hasChanged: false });
      setPageHeading(info?.name ? info.name : "Redigera annons");
    } else {
      // Else fetch ad info
      fetchFormData();
    }

    // eslint-disable-next-line
  }, [location]);

  // Disable submit and preview if required fields are empty
  useEffect(() => {
    setDisableSubmit(!requiredFieldsCheck(formData, newImages));
  }, [formData, newImages]);

  const saveAdHandler = (activate) => {
    const active =
      activate === "activate"
        ? true
        : activate === "inactivate"
          ? false
          : formData?.active;

    const isValid = authenticateForm(formData, newImages, setInvalidFields);
    if (isValid) {
      setLoading(true);
      saveChangesRequest(active);
    }
  };

  const saveChangesRequest = async (active) => {
    try {
      const { data } = await axios.patch(backend("/api/listings/edit"), {
        token: localStorage.getItem("token"),
        ad_id: id,
        data: {
          ...formData,
          images: [...formData?.images, ...newImages],
          active: active,
        },
      });

      if (data.status) {
        uploadImages();
      }
    } catch (error) {
      logAxiosError(error);
      setLoading(false);
      setErrorMessage(error.response?.data?.msg || "");
      setShowErrorModal(true);
      if (error.response?.status === 401) {
        // invalid JWT token -> log out and return to landing page
        auth.logout();
      }
    }
  };

  const uploadImages = async () => {
    let errorOccured = false;

    for (let image of newImages) {
      try {
        // Validera filtyp innan uppladdning (defense-in-depth)
        const isValidImage = await checkMagicBytes(image.file);
        if (!isValidImage) {
          console.error(
            "Ogiltig filtyp blockerad vid uppladdning:",
            image.file.name,
          );
          errorOccured = true;
          continue;
        }

        const imageData = new FormData();
        imageData.append("file", image.file);
        imageData.append("upload_preset", "oi2aoqjs");
        imageData.append("public_id", `/${id}/${image.id}`);

        const response = await axios.post(
          "https://api.cloudinary.com/v1_1/vakansa/image/upload",
          imageData,
        );
        if (response.status !== 200) {
          errorOccured = true;
        }
      } catch (error) {
        console.error("Bilduppladdning misslyckades för:", image.id, error);
        errorOccured = true;
      }
    }

    // All steps are done, set loading to false
    // and set custom error message if errors occured
    setLoading(false);
    setErrorMessage(
      errorOccured
        ? "Ett problem uppstod under bild uppladdningen. Det kan ha hänt att inte alla bilder blev uppladdade."
        : "",
    );

    // Show modal passed as argument
    if (!errorOccured) {
      setShowPrompt(false);
      setShowSaveChangesModal(true);
      setAdPreviewInfo({ info: {}, images: [], hasChanged: false });
    } else {
      setShowPrompt(true);
      setShowErrorModal(true);
    }
  };

  // Run on every on change handler
  const formHasChanged = () => {
    // Set hasChanged to true after changing form
    setAdPreviewInfo((prev) => ({ ...prev, hasChanged: true }));

    // Show prompt after form has changed
    setShowPrompt(true);
  };

  const onChangeHandler = (e) => {
    const isNumericInput = e.target.inputMode === "numeric";
    const isNumericValue = new RegExp(/^[0-9]*$/).test(e.target.value);

    // Don't set value if non-numeric value (excl. spaces)
    // is typed into a numeric text field. For example:
    // <input type="text" inputMode="numeric" />
    if (isNumericInput && !isNumericValue) return;

    // Run on every valid onChange
    formHasChanged();

    // Else set value as usual
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  const onImagesChange = async (e) => {
    const files = e.target.files;

    // User can only add max 12 images at once (max is 12)
    if (files.length < 1 || files.length > 12) return;
    if (newImages.length + files.length > 12) {
      setImageTooBig(
        "Du kan inte ladda upp fler än 12 bilder. Du har nått maximum",
      );
      return;
    }

    // Rensa tidigare valideringsfel
    clearValidationErrors();

    // Använd hook för validering
    const { rejectedImages, errors } = await validateFiles([...files]);

    // Skapa objekt från giltiga filer
    const validFiles = [...files].filter(
      (file) => !rejectedImages.includes(file),
    );
    const newImageObjects = validFiles
      .map((file) => {
        // Check för duplikat
        const existingImage = newImages.find(
          (image) => image.file.name === file.name,
        );
        if (existingImage) {
          setImageTooBig(`Image "${file.name}" already exists in the list`);
          return null;
        }
        return {
          url: URL.createObjectURL(file),
          id: uuidv4(),
          file,
        };
      })
      .filter(Boolean); // Filtrera bort null värden

    // Hantera felmeddelanden
    if (rejectedImages.length > 0) {
      const errorMsg =
        errors.length > 0
          ? errors.join(", ")
          : rejectedImages.length === 1
            ? `1 bild avvisades`
            : `${rejectedImages.length} bilder avvisades`;
      setImageTooBig(errorMsg);
    } else {
      setImageTooBig(null);
    }

    // Run on every valid onChange
    formHasChanged();

    setNewImages((prevData) => [...prevData, ...newImageObjects]);
  };

  // For removing images the user has just added
  const removeNewImageHandler = (image) => {
    // Run on every valid onChange
    formHasChanged();

    setNewImages((prevData) =>
      prevData.filter((currentImage) => currentImage.id !== image),
    );
  };

  // For removing images already stored in cloudinary
  const removeOldImageHandler = (image) => {
    // Run on every valid onChange
    formHasChanged();

    setFormData((prevData) => ({
      ...prevData,
      images: formData?.images.filter((currentImage) => currentImage !== image),
    }));
  };

  useEffect(() => {
    if (!viewPreview && !showPrompt) return;

    // Navigate to preview page
    navigate("/mina-sidor/redigera-annons/forhandsvy", {
      state: { prevRoute: location.pathname },
    });
    // eslint-disable-next-line
  }, [viewPreview]);

  const adPreviewHandler = () => {
    // Disable prompt
    setShowPrompt(false);

    // Set the AdPreviewInfo (context) to use on preview page
    setAdPreviewInfo((prev) => ({
      ...prev,
      info: formData,
      images: newImages,
    }));

    // Navigate to preview page
    setViewPreview(true);
  };

  const validateField = (e) => {
    const { value, name } = e.target;
    const defaultDropdownValues = ["Välj kommun", "Typ av lokal"];
    const invalidDropdownValue = defaultDropdownValues.includes(value);

    if (
      !value ||
      invalidDropdownValue ||
      (name === "zip_code" && !/^\d{5}$/.test(value))
    ) {
      setInvalidFields([...invalidFields, name]);
    } else {
      setInvalidFields(invalidFields.filter((field) => field !== name));
    }

    //special case check for the price group
    if (
      ["hourly_rent", "daily_rent", "monthly_rent", "workspace_rent"].includes(
        name,
      )
    ) {
      if (hasValidPrice(formData)) {
        setInvalidFields(invalidFields.filter((field) => field !== "rent"));
      } else {
        setInvalidFields([
          ...invalidFields.filter((field) => field !== "rent"),
          "rent",
        ]);
      }
    }
  };

  const handleImageSelect = (index) => {
    if (index >= formData?.images?.length) {
      setImageError(
        "Om du laddar upp en ny bild vänligen spara ändringarna i annonsen för att kunna sortera bilderna.",
      );
      setSelectedImageIndex((prevIndex) =>
        prevIndex === index ? null : index,
      );
      return;
    }
    setImageError(null);
    setSelectedImageIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const moveImage = (index, direction) => {
    if (selectedImageIndex === null) return;

    const images = [...formData.images];

    if (direction === "left" && index > 0) {
      const [movedImage] = images.splice(index, 1);
      images.splice(index - 1, 0, movedImage);
      setSelectedImageIndex(selectedImageIndex - 1);
    } else if (direction === "right" && index < images.length - 1) {
      const [movedImage] = images.splice(index, 1);
      images.splice(index + 1, 0, movedImage);
      setSelectedImageIndex(selectedImageIndex + 1);
    } else {
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      images,
    }));
  };

  // Show error if less than 4 images
  useEffect(() => {
    const totalImages = (formData?.images?.length || 0) + newImages.length;
    if (totalImages < 4) {
      setImageCountError("Minst 4 bilder krävs för att kunna spara annonsen.");
    } else {
      setImageCountError("");
    }
  }, [formData.images, newImages]);

  const tooltipText = {
    moms: "Momsad: Moms läggs på hyran<br/>Icke momsad: Lokalen hyrs ut utan moms",
  };

  const renderTooltipIcon = (tooltipHtml) => (
    <svg
      aria-hidden="true"
      data-tooltip-id="my-tooltip"
      data-tooltip-html={tooltipHtml}
      viewBox="0 0 512 512"
      fill="currentColor"
      height="24px"
      width="24px"
      {...props}
    >
      <path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0 0 114.6 0 256s114.6 256 256 256zm-86.2-346.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1 0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6-13.3 0-24-10.7-24-24v-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1 0-8.4-6.8-15.1-15.1-15.1h-58.3c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM288 352c0 17.7-14.3 32-32 32s-32-14.3-32-32 14.3-32 32-32 32 14.3 32 32z" />
    </svg>
  );

  return (
    pageHeading && ( // Only show once listing data is fetched
      <>
        <Helmet>
          <title>Redigera annons | Vakansa</title>
          <meta
            name="description"
            content="Redigera och publicera din annons enkelt på Vakansa för att nå potentiella hyresgäster."
          />
          <link
            rel="canonical"
            href={backend(
              `/mina-sidor/annonser/redigera-annons/${formData._id}`,
            )}
          />
          <meta property="og:image" content="/images/logo/meta-logo.jpg" />
          <meta property="og:title" content="Redigera en annons på Vakansa" />
          <meta
            property="og:description"
            content="Redigera och publicera din annons enkelt på Vakansa för att nå potentiella hyresgäster."
          />
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="edit-ad-container">
          <span className="create-ad-inline-msg">
            (Fält markerade med * är obligatoriska och måste fyllas i)
          </span>
          <br />
          <br />
          <br />
          <form>
            <div className="edit-ad-input-row">
              <div className="edit-ad-input-col edit-ad-input-col-lg">
                <label htmlFor="editAdOwner">
                  <div>
                    Fastighetsägare*{" "}
                    <span className="edit-ad-information-italics">
                      (Detta fält publiceras inte i annonsen)
                    </span>
                  </div>
                  <input
                    className="edit-ad-input-fastighet"
                    type="text"
                    id="editAdAddress"
                    name="owner_name"
                    placeholder="Fastighetsägare"
                    value={formData?.owner_name}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                  {invalidFields.includes("owner_name") && (
                    <span className="edit-ad-error-msg">
                      Ange en giltig fastighetsägare{" "}
                    </span>
                  )}
                </label>
              </div>
            </div>
            <div className="edit-ad-input-row">
              <div className="edit-ad-input-col edit-ad-input-col-lg">
                <label htmlFor="editAdAddress">
                  Adress*
                  <input
                    type="text"
                    id="editAdAddress"
                    name="address"
                    placeholder="Adress"
                    value={formData?.address}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                  {invalidFields.includes("address") && (
                    <span className="edit-ad-error-msg">
                      Ange en giltig adress{" "}
                    </span>
                  )}
                </label>
              </div>
              <div className="edit-ad-input-col edit-ad-input-col-md">
                <label htmlFor="editAdCity">
                  Stad*
                  <input
                    type="text"
                    id="editAdCity"
                    name="city"
                    placeholder="Stad"
                    value={formData?.city}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                  {invalidFields.includes("city") && (
                    <span className="edit-ad-error-msg">Ange stad</span>
                  )}
                </label>
              </div>
              <div className="edit-ad-input-col edit-ad-input-col-md">
                <label htmlFor="editAdMunicipality">
                  Kommun*
                  <div className="edit-ad-select-wrapper">
                    <select
                      id="editAdMunicipality"
                      name="municipality"
                      // defaultValue="Välj kommun"
                      value={formData?.municipality}
                      onChange={onChangeHandler}
                      onBlur={validateField}
                    >
                      <option hidden>Välj kommun</option>
                      <option>Stockholm </option>
                      <option>Göteborg </option>
                      <option>Malmö </option>
                      <option>Uppsala</option>
                      <optgroup label="Alla kommuner">
                        {municipalities.map((item, i) => (
                          <option key={i} value={item}>
                            {item}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    <img src="/images/icons/arrow-down.svg" alt="arrow-down" />
                  </div>
                  {invalidFields.includes("municipality") && (
                    <span className="edit-ad-error-msg">Ange kommun</span>
                  )}
                </label>
              </div>
              <div className="edit-ad-input-col edit-ad-input-col-sm">
                <label htmlFor="editAdZip">
                  Postkod*
                  <input
                    type="text"
                    inputMode="numeric"
                    id="editAdZip"
                    name="zip_code"
                    placeholder="Postkod"
                    value={formData?.zip_code}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                  {invalidFields.includes("zip_code") && (
                    <span className="edit-ad-error-msg">
                      Ange en giltig postkod (5 siffror){" "}
                    </span>
                  )}
                </label>
              </div>
            </div>
            <div className="edit-ad-input-row">
              <div className="edit-ad-input-col edit-ad-input-col-md">
                <label htmlFor="editAdType">
                  Lokal*
                  <div className="edit-ad-select-wrapper edit-ad-input-col-md">
                    <select
                      id="editAdType"
                      name="type_of_room"
                      value={formData?.type_of_room}
                      onChange={onChangeHandler}
                      onBlur={validateField}
                    >
                      <option hidden>Typ av lokal</option>
                      {roomTypes.map((roomType, i) => (
                        <option key={i}>{roomType}</option>
                      ))}
                    </select>
                    <img src="/images/icons/arrow-down.svg" alt="arrow-down" />
                  </div>
                  {invalidFields.includes("type_of_room") && (
                    <span className="edit-ad-error-msg">Ange lokaltyp</span>
                  )}
                </label>
              </div>
              <div className="edit-ad-input-col edit-ad-input-col-sm">
                <label htmlFor="editAdArea">
                  Yta*
                  <input
                    id="editAdArea"
                    name="area"
                    type="text"
                    inputMode="numeric"
                    placeholder="kvm"
                    value={formData?.area}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                  {invalidFields.includes("area") && (
                    <span className="edit-ad-error-msg">Ange yta</span>
                  )}
                </label>
              </div>
              <div className="edit-ad-input-col edit-ad-input-col-md">
                <label htmlFor="editAdMaxPeople">
                  Lämpligt för antal personer
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="editAdMaxPeople"
                  name="capacity"
                  placeholder="st"
                  value={formData?.capacity}
                  onChange={onChangeHandler}
                />
              </div>
              <div className="edit-ad-input-col edit-ad-input-col-md">
                <label htmlFor="editAdWorkspaces">Antal sittplatser</label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="editAdWorkspaces"
                  name="workspaces"
                  placeholder="st"
                  value={formData?.workspaces}
                  onChange={onChangeHandler}
                />
              </div>
            </div>
            <div className="edit-ad-input-row">
              <div className="edit-ad-input-col edit-ad-input-col-xl">
                <label htmlFor="editAdContractType">Kontraktstyp</label>
                <div className="edit-ad-select-wrapper">
                  <select
                    id="editAdContractType"
                    className="contract-type-select"
                    name="contract_type"
                    value={formData?.contract_type}
                    onChange={onChangeHandler}
                  >
                    <option value="">Välj kontraktstyp</option>
                    <option value="Förstahandskontrakt">
                      Förstahandskontrakt
                    </option>
                    <option value="Andrahandskontrakt">
                      Andrahandskontrakt
                    </option>
                  </select>
                  <img src="/images/icons/arrow-down.svg" alt="arrow-down" />
                </div>
              </div>
              <div className="edit-ad-input-col edit-ad-input-col-md">
                <div className="edit-ad-vat-tax-wrapper">
                  <fieldset className="radio-group">
                    <legend>
                      Hyrs lokalen ut med moms eller utan moms?{" "}
                      {renderTooltipIcon(tooltipText.moms)}
                    </legend>
                    <span id="moms-info" className="visually-hidden">
                      {tooltipText.moms}
                    </span>
                    <div>
                      <label className="radio-label">
                        <input
                          type="radio"
                          value="true"
                          className="radio-button"
                          checked={formData?.vat_tax_required === true}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              vat_tax_required: true,
                            }))
                          }
                        />
                        &nbsp; Momsad
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          value="false"
                          className="radio-button"
                          checked={formData?.vat_tax_required === false}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              vat_tax_required: false,
                            }))
                          }
                        />
                        &nbsp; Icke momsad
                      </label>
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
            <FacilitiesForm formData={formData} setFormData={setFormData} />
            <div className="edit-ad-input-row">
              <div className="edit-ad-input-col edit-ad-input-col-xxl">
                <h5 htmlFor="editAdDescription">Beskrivning av lokalen*</h5>
                <p>
                  Lyft här information som inte inkluderas här ovan, tex hur man
                  tar sig till lokalen, vad som gör den unik och vilka
                  aktiviteter som inte kan göras här.
                </p>
                <p>
                  <i>
                    Vänligen ange på ett ungefär vilka dagar och tider lokalen
                    ska hyras ut, eller om den är helt vakant.
                  </i>
                </p>
                <p>
                  <strong>OBS! </strong>Det är inte tillåtet att uppge
                  kontaktuppgifter eller andra ledande uppgifter i
                  beskrivningstexten som skulle göra att ni kringgår Vakansas
                  tjänster och våra{" "}
                  <a target="_blank" href="/anvandarvillkor">
                    användarvillkor
                  </a>
                </p>
                <textarea
                  id="editAdDescription"
                  name="description"
                  className="form-control"
                  placeholder="Skriv här..."
                  value={formData?.description}
                  onChange={onChangeHandler}
                  onBlur={validateField}
                  onInput={autoExpand}
                />
                {invalidFields.includes("description") && (
                  <span className="edit-ad-error-msg">Ange beskrivning </span>
                )}
              </div>
            </div>
            <div>
              <h5>Lägg till bilder</h5>
              <p>
                Ladda upp minst <strong>4 bilder</strong> för att ge en bra
                presentation av lokalen. Du kan bifoga upp till{" "}
                <strong>12 bilder</strong>.
              </p>
              <p>
                För bästa resultat rekommenderar vi att du inkluderar en
                planritning av lokalen.
              </p>
              <p>
                <b>Maximal filstorlek per bild:</b> 10 MB. Vi stödjer följande
                format: <strong>JPG, PNG, WEBP och GIF</strong>.
              </p>
              <p className="edit-ad-image-italics">
                Den första bilden du laddar upp blir annonsens huvudbild.{" "}
                <span>
                  Ordningen på bilderna kan du senare ändra i redigera annons
                  efter att du skapat annonsen
                </span>
              </p>

              {ImageTooBig !== null && (
                <h3
                  style={{
                    color: "red",
                    fontSize: "medium",
                    marginBottom: "1rem",
                  }}
                >
                  {ImageTooBig}
                </h3>
              )}
              <div className="edit-ad-image-container">
                {/* Images fetched from Cloudinary */}
                {formData?.images &&
                  formData?.images.map((image, i) => (
                    <div
                      key={`fetched-${i}`}
                      className={`edit-ad-image ${
                        selectedImageIndex === i ? "selected" : ""
                      }`}
                      onClick={() => handleImageSelect(i)}
                    >
                      <Image
                        key={`fetched-${i}`}
                        cloudName="vakansa"
                        publicId={`listings/${formData?._id}/${image}`}
                        dpr="auto"
                        className="edit-ad-fetched-image"
                      >
                        <Transformation crop="fill" gravity="center" />
                      </Image>
                      <button
                        type="button"
                        onClick={() => removeOldImageHandler(image)}
                        className="edit-ad-image-delete-button"
                      >
                        {/* <svg
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          height="2rem"
                          {...props}
                        >
                          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm3.7 12.3c.4.4.4 1 0 1.4-.4.4-1 .4-1.4 0L12 13.4l-2.3 2.3c-.4.4-1 .4-1.4 0-.4-.4-.4-1 0-1.4l2.3-2.3-2.3-2.3c-.4-.4-.4-1 0-1.4.4-.4 1-.4 1.4 0l2.3 2.3 2.3-2.3c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4L13.4 12l2.3 2.3z" />
                        </svg> */}
                        <svg
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          height="2.2em"
                          width="1.6em"
                          {...props}
                        >
                          <path d="M13.41 12l4.3-4.29a1 1 0 10-1.42-1.42L12 10.59l-4.29-4.3a1 1 0 00-1.42 1.42l4.3 4.29-4.3 4.29a1 1 0 000 1.42 1 1 0 001.42 0l4.29-4.3 4.29 4.3a1 1 0 001.42 0 1 1 0 000-1.42z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                {/* Local images added by the user */}
                {newImages.map((image, i) => (
                  <div
                    key={`local-${i}`}
                    className={`edit-ad-image ${
                      selectedImageIndex === formData?.images?.length + i
                        ? "selected-local"
                        : ""
                    }`}
                    onClick={() =>
                      handleImageSelect(formData?.images?.length + i)
                    }
                  >
                    <img
                      src={image.url}
                      alt=""
                      className="edit-ad-added-image"
                    />
                    <button
                      type="button"
                      onClick={() => removeNewImageHandler(image.id)}
                      className="edit-ad-image-delete-button"
                    >
                      {/* <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        height="2rem"
                        {...props}
                      >
                        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm3.7 12.3c.4.4.4 1 0 1.4-.4.4-1 .4-1.4 0L12 13.4l-2.3 2.3c-.4.4-1 .4-1.4 0-.4-.4-.4-1 0-1.4l2.3-2.3-2.3-2.3c-.4-.4-.4-1 0-1.4.4-.4 1-.4 1.4 0l2.3 2.3 2.3-2.3c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4L13.4 12l2.3 2.3z" />
                      </svg> */}
                      <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        height="2.2em"
                        width="1.6em"
                        {...props}
                      >
                        <path d="M13.41 12l4.3-4.29a1 1 0 10-1.42-1.42L12 10.59l-4.29-4.3a1 1 0 00-1.42 1.42l4.3 4.29-4.3 4.29a1 1 0 000 1.42 1 1 0 001.42 0l4.29-4.3 4.29 4.3a1 1 0 001.42 0 1 1 0 000-1.42z" />
                      </svg>
                    </button>
                  </div>
                ))}
                {formData?.images.length + newImages.length < 12 && (
                  <>
                    <input
                      type="file"
                      name="images"
                      id="edit-ad-image-input"
                      multiple
                      accept="image/*"
                      onChange={onImagesChange}
                    />
                    <label
                      className="edit-ad-custom-file-input"
                      htmlFor="edit-ad-image-input"
                    >
                      <img
                        src="/images/icons/plus-circle.svg"
                        alt="plus with a circle"
                      />
                      <span>Välj bild</span>
                    </label>
                  </>
                )}
              </div>
              {imageError && (
                <div className="edit-ad-image-error-msg">{imageError}</div>
              )}

              <div className="edit-ad-btn-wrapper-image-change-order">
                <RoundedButton
                  onClick={(e) => {
                    e.preventDefault();
                    moveImage(selectedImageIndex, "left");
                  }}
                  disabled={
                    selectedImageIndex === 0 ||
                    selectedImageIndex === null ||
                    selectedImageIndex >= formData?.images?.length
                  }
                  size="xs"
                  label={"<"}
                />
                <RoundedButton
                  onClick={(e) => {
                    e.preventDefault();
                    moveImage(selectedImageIndex, "right");
                  }}
                  disabled={
                    selectedImageIndex === formData.images.length - 1 ||
                    selectedImageIndex === null ||
                    selectedImageIndex >= formData?.images?.length
                  }
                  size="xs"
                  label={">"}
                />
              </div>
              {imageCountError && (
                <p className="create-ad-error-msg img-error">
                  {imageCountError}
                </p>
              )}
              <h5>Ange pris*</h5>
              <p>(fyll i minst 1 fält)</p>
              <div className="edit-ad-input-price-row">
                <div className="edit-ad-input-col edit-ad-input-col-md">
                  <label htmlFor="editAdMinPrice">
                    Timpris (enstaka timmar)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="hourly_rent"
                    id="editAdMinPrice"
                    placeholder="kr/timme"
                    value={formData?.hourly_rent}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                </div>
                <div className="edit-ad-input-col edit-ad-input-col-md">
                  <label htmlFor="editAdMaxPrice">
                    Dagspris (8 timmar i rad)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="editAdMaxPrice"
                    name="daily_rent"
                    placeholder="kr/dag"
                    value={formData?.daily_rent}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                </div>
                <div className="edit-ad-input-col edit-ad-input-col-md">
                  <label htmlFor="editAdMaxPrice">
                    Månadspris (30 dagar i rad)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="editAdMaxPrice"
                    name="monthly_rent"
                    placeholder="kr/månad"
                    value={formData?.monthly_rent}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                </div>
                <div className="edit-ad-input-col edit-ad-input-col-md">
                  <label htmlFor="editAdMaxPrice">
                    Pris per arbetsplats (per månad)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="editAdMaxPrice"
                    name="workspace_rent"
                    placeholder="kr/arbetsplats"
                    value={formData?.workspace_rent}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                </div>
              </div>
              {invalidFields.includes("rent") && (
                <span className="edit-ad-error-msg">
                  Alla ifyllda prisfält måste vara större än 0 kr{" "}
                </span>
              )}
            </div>
            <div className="edit-ad-form-customer-info">
              <p>
                Kom ihåg att Vakansa tar 15% av hyresintäkterna vid lyckad
                uthyrning av er lokal. Läs mer om vår prismodell {""}
                <a target="_blank" rel="noreferrer" href="/hyr-ut-lokal">
                  här.
                </a>
              </p>
            </div>
            <div className="edit-ad-form-buttons">
              {disableSubmit && (
                <p className="edit-ad-form-buttons-disabled-info">
                  *Alla obligatoriska fält måste vara ifyllda för att kunna gå
                  vidare. Scrolla upp för mer information.
                </p>
              )}
              <div className="edit-ad-button-row">
                <RoundedButton
                  color="white"
                  label="Förhandsvy"
                  size="lg"
                  onClick={adPreviewHandler}
                  disabled={disableSubmit}
                  type="button"
                />
                {formData?.active ? (
                  <RoundedButton
                    color="white"
                    label="Inaktivera annons"
                    size="lg"
                    onClick={() => saveAdHandler("inactivate")}
                    disabled={disableSubmit}
                    type="button"
                  />
                ) : (
                  <RoundedButton
                    color="white"
                    label="Aktivera annons"
                    size="lg"
                    onClick={() => saveAdHandler("activate")}
                    disabled={disableSubmit}
                    type="button"
                  />
                )}

                <RoundedButton
                  label="Spara ändringar"
                  size="lg"
                  color="green"
                  onClick={saveAdHandler}
                  disabled={disableSubmit}
                  type="button"
                />
              </div>
            </div>
          </form>
          <ReactTooltip className="tooltip" id="my-tooltip" />
        </div>
        {/* Spinner while ad is being created/updated */}
        {loading && <Loading text="Sparar ändringar..." />}
        {showErrorModal && (
          <ConfirmationModal
            heading="Någonting gick fel..."
            text={
              errorMessage ||
              "Ett problem uppstod med när dina ändringar sparades. Vänligen prova igen senare."
            }
            setShowModal={setShowErrorModal}
            error
          />
        )}
        {showSaveChangesModal && (
          <ConfirmationModal
            heading="Dina ändringar har sparats"
            text='Gå till "mina annonser" under Mina annonser för att fortsätta redigera annonsen.'
            linkTo="/mina-sidor/annonser"
            setShowModal={setShowSaveChangesModal}
            note={errorMessage ? errorMessage : ""}
          />
        )}
        {showWarningModal && (
          <WarningModal
            heading="Lämna utan att spara"
            text="Är du säker att du vill lämna sidan? Dina ändringar kommer ej att sparas."
            confirmBtnText="Lämna"
            onCancel={cancelNavigation}
            onConfirm={() => {
              setAdPreviewInfo({ info: {}, images: [], hasChanged: false });
              confirmNavigation();
            }}
          />
        )}
      </>
    )
  );
}

export default EditAd;
